<template>
  <div class="tree">
    <div class="citation">{{ tree.citation }}</div>
    <Sentence
      :words="tree.words"
      :selected="selected"
      @word-enter="onWordEnter"
      @word-leave="onWordLeave"
    />
    <!-- TODO: Determine best extensibility hooks here -->
    <div v-if="hasTranslations" class="collapse-control">
      <a href @click.prevent="onTranslationCollapse">{{
        translationCollapsed
          ? `Show ${translationsLabel}`
          : `Hide ${translationsLabel}`
      }}</a>
    </div>
    <template v-if="hasTranslations && !translationCollapsed">
      <Translation
        v-for="(translation, idx) in translations"
        :key="idx"
        :content="translation[0]"
        :lang="translation[1]"
      />
    </template>
    <div class="collapse-control">
      <a href @click.prevent="onTreeCollapse">{{
        treeCollapsed ? 'Show Tree' : 'Hide Tree'
      }}</a>
    </div>
    <Treant
      v-if="!treeCollapsed"
      class="syntax-tree"
      :redrawKey="sideBarState"
      :treeBankId="tree.treeBankId"
      :tree="tree.tree"
      :highlighted="selected"
      @enter="onEnter"
      @leave="onLeave"
    />
    <div class="collapse-control metadata-control">
      <a href @click.prevent="onMetadataCollapse">{{
        metadataCollapsed ? 'Show Metadata' : 'Hide Metadata'
      }}</a>
    </div>
    <TreeMetadata v-if="!metadataCollapsed" :tree="tree" />
  </div>
</template>

<script>
  import Treant from '@scaife-viewer/vue-treant';
  import { MODULE_NS } from '@scaife-viewer/store';

  import Translation from './Translation.vue';
  import TreeMetadata from './TreeMetadata.vue';
  import Sentence from './Sentence.vue';

  export default {
    props: ['tree', 'expanded', 'first'],
    components: {
      Sentence,
      Translation,
      Treant,
      TreeMetadata,
    },
    data() {
      return {
        hoveringOn: null,
        treeCollapsed: true,
        metadataCollapsed: true,
        translationCollapsed: true,
      };
    },
    watch: {
      first: {
        immediate: true,
        handler() {
          if (this.first && this.expanded === null) {
            this.treeCollapsed = false;
            this.translationCollapsed = false;
          }
        },
      },
      expanded: {
        immediate: true,
        handler() {
          if (this.expanded !== null && this.treeCollapsed === this.expanded) {
            this.treeCollapsed = !this.expanded;
          }
        },
      },
    },
    methods: {
      onMetadataCollapse() {
        this.metadataCollapsed = !this.metadataCollapsed;
      },
      onTranslationCollapse() {
        this.translationCollapsed = !this.translationCollapsed;
      },
      onTreeCollapse() {
        this.treeCollapsed = !this.treeCollapsed;
        this.$emit('tree-collapsed');
      },
      onWordEnter({ id }) {
        this.hoveringOn = id;
      },
      onWordLeave() {
        this.hoveringOn = null;
      },
      onEnter({ id }) {
        this.hoveringOn = id;
      },
      onLeave() {
        this.hoveringOn = null;
      },
    },
    computed: {
      hasTranslations() {
        return this.translations.length > 0;
      },
      translationsLabel() {
        // TODO: Revisit the best pluralize implementation and use it throghout
        return this.translations.length > 1 ? 'Translations' : 'Translation';
      },
      translations() {
        return this.tree.translations || [];
      },
      selected() {
        return {
          word: this.hoveringOn,
          parent: this.selectedParent,
          children: this.selectedChildren,
        };
      },
      wordIndex() {
        return this.tree.words.reduce(
          (map, word) => ({
            ...map,
            [word.id]: word,
          }),
          {},
        );
      },
      selectedWord() {
        return this.wordIndex[this.hoveringOn];
      },
      selectedChildren() {
        return this.selectedWord && this.selectedWord.children.map(c => c.id);
      },
      selectedParent() {
        return this.selectedWord && this.selectedWord.headId;
      },
      sideBarState() {
        const {
          rightOpen,
          leftOpen,
          leftVisible,
          rightVisible,
        } = this.$store.state[MODULE_NS];
        return `${rightOpen}-${leftOpen}-${leftVisible}-${rightVisible}`;
      },
    },
  };
</script>

<style lang="scss" scoped>
  // TODO: factor this out
  $interlinear-font-family: 'Lucida Console', Monaco, monospace;
  .tree {
    margin: 1em 0;
    .collapse-control {
      font-size: 80%;
      margin-bottom: 0.5em;
    }
    .citation {
      text-align: center;
      font-size: 12pt;
      color: var(--sv-reader-syntax-tree-node-refs-text-color, #69c);
      font-family: var(
        --sv-reader-syntax-tree-node-refs-font-family,
        'Noto Sans'
      );
      margin-bottom: 5px;
    }
  }
  .syntax-tree::v-deep {
    .node.highlight .node-attrs {
      color: var(--sv-reader-syntax-trees-mode-highlight-text-color, #d44);
      background: var(
        --sv-reader-syntax-trees-mode-highlight-background-color,
        #fea
      );
      border-color: var(
        --sv-reader-syntax-trees-mode-highlight-text-color,
        #d44
      );
    }

    .node-attrs {
      margin-bottom: 20px;
      border-bottom: 3px solid transparent;
      // TODO: pt vs px, overall size
      font-size: 16pt;
      line-height: 1.8em;
      > div {
        margin-bottom: 0;
      }
    }
    .node.highlight-parent {
      color: var(--sv-reader-syntax-trees-mode-parent-border-color, #66f);
    }
    .node.highlight-child {
      color: var(--sv-reader-syntax-trees-mode-child-border-color, #3c3);
    }
    .node-relation {
      color: var(--sv-reader-syntax-tree-node-relation-text-color, #aaa);
      margin-bottom: 0;
      margin-top: 2px;
      font-size: 10pt;
    }
    .node-value {
      margin-top: 0;
      font-size: 1em;
      font-family: var(
        --sv-reader-syntax-tree-node-value-font-family,
        'Noto Serif'
      );
    }
    .node-transliterated-text {
      margin-top: 0;
      font-size: 1em;
      font-family: Arial;
    }
    .node-lemma {
      font-size: 0.88em;
      color: var(--sv-reader-syntax-tree-node-lemma-text-color, #aaa);
    }
    .node-gloss {
      font-size: 0.88em;
      color: var(--sv-reader-syntax-tree-node-gloss-text-color, #aaa);
      font-style: italic;
    }
    .node-gloss-rtl {
      direction: rtl;
      font-family: var(
        --sv-reader-syntax-tree-node-gloss-font-family-rtl,
        'Amiri',
        'Noto Sans'
      );
      font-style: initial;
      font-size: 24px;
      line-height: 1.7;
    }
    .node-tag {
      font-family: var(
        --sv-reader-syntax-trees-node-tag-font-family,
        $interlinear-font-family
      );
      font-size: 0.66em;
      color: var(--sv-reader-syntax-trees-node-tag-text-color, #999);
    }
    .node {
      text-align: center;
    }
    .collapse-switch {
      width: 15px;
      height: 15px;
      line-height: 1;
      bottom: 0;
      top: unset;
      right: calc(50% - 8px);
      color: unset;
      border-radius: 15px;
      border-color: var(
        --sv-reader-syntax-tree-node-collapse-open-border-color,
        #aaa
      );
      &:hover {
        border-color: var(
          --sv-reader-syntax-tree-node-collapse-open-hover-border-color,
          #999
        );
        background-color: var(
          --sv-reader-syntax-tree-node-collapse-open-hover-background-color,
          #eee
        );
      }
    }
    .collapsed .collapse-switch {
      background: unset;
      border-color: var(
        --sv-reader-syntax-tree-node-collapse-closed-border-color,
        #5394ca
      );
      background-color: var(
        --sv-reader-syntax-tree-node-collapse-closed-background-color,
        #6bf
      );

      &:hover {
        border-color: var(
          --sv-reader-syntax-tree-node-collapse-closed-hover-border-color,
          #999
        );
        background-color: var(
          --sv-reader-syntax-tree-node-collapse-closed-hover-border-color,
          unset
        );
      }
    }
  }
</style>
